import { TextArea, TextField } from '@radix-ui/themes'
import styled, { css } from 'styled-components'

export const StyledInputContainer = styled.div<{ $labelLocation?: string }>`
  align-items: center;
  display: ${(props) => (props.$labelLocation === 'side' ? 'grid' : 'flex')};
  grid-template-columns: 1fr 1fr;
  align-items: ${(props) =>
    props.$labelLocation === 'side' ? 'center' : 'flex-start'};
  flex-direction: ${(props) =>
    props.$labelLocation === 'side' ? 'row' : 'column'};
  gap: ${(props) => (props.$labelLocation === 'side' ? '0.5rem' : '0rem')};
  width: 100%;

  @media (max-width: 950px) {
    display: block;
  }
`

export const StyledTextFieldSlot = styled(TextField.Slot)`
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export function buildInputDescribedBy(
  ...ids: Array<string | false | null | undefined>
): string | undefined {
  const describedBy = ids.filter(Boolean).join(' ')
  return describedBy || undefined
}

export const StyledLabel = styled.label<{ $labelColor?: string }>`
  display: flex;
  flex: 0 1 auto;
  color: ${({ theme, $labelColor }) =>
    $labelColor !== undefined ? $labelColor : theme.forecast.secondaryText};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25rem;
  min-height: 2rem;
  margin-bottom: 0.25rem;
  align-items: center;
`

export const TextValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 0 1 auto;
  height: 32px;
  padding: var(--Padding-1_5, 6px) var(--Padding-1, 4px);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  @media (max-width: 950px) {
    overflow-wrap: break-word;
    word-break: break-all;
    white-space: normal;
    height: fit-content;
  }
`

export const SubtitleRowStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.25 rem 0px;
  gap: 0.25 rem;
  left: 0%;
  right: 0%;
  bottom: 0%;
`

export const SubtitleTextStyled = styled.div<{
  $subtitleTextColor?: string
}>`
  font-family: ${({ theme }) => theme.fontFamily.manrope};
  font-style: normal;
  font-weight: 500;
  line-height: 125%;
  margin-top: 0.25rem;
  color: ${(props) =>
    props.$subtitleTextColor ? props.$subtitleTextColor : 'white'};
`

export const InputAndSubtitle = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const CheckIconContainer = styled.div`
  display: flex;
  width: 20%;
  align-items: center;
  color: ${({ theme }) => theme.forecast.defaultText};
`

export const StyledDropdownItem = styled.div<{
  $color?: string
  $backgroundColor?: string
  $borderColor?: string
}>`
  padding: 0.25rem;

  font-family: ${({ theme }) => theme.fontFamily.manrope};
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor ? $backgroundColor : theme.forecast.cardGroupBackground};
  border-width: ${(props) => (props.$borderColor ? '1px' : 'none')};
  border-radius: 0.25rem;
  border-color: ${(props) =>
    props.$borderColor ? props.$borderColor : 'none'};
  border-style: ${(props) => (props.$borderColor ? 'solid' : 'none')};
  font-weight: 600;
  opacity: 1;

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:focus {
    cursor: initial;
  }

  &:disabled {
    cursor: not-allowed;
  }
`

export interface TextInputProps extends React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> {
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  $placeholderColor?: string
  mode?: 'read' | 'edit'
  type?: 'input' | 'textarea' | 'link'
  ref?: React.ForwardedRef<HTMLInputElement>
}

export const sharedPickerFocusStyles = css`
  .rs-picker.rs-picker-focused,
  .rs-picker-toggle-active {
    border-color: ${({ theme }) => theme.forecast.activeText} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.forecast.activeText} !important;
  }

  .rs-picker-default:not(.rs-picker-disabled) .rs-picker-toggle:hover,
  .rs-picker-default:not(.rs-picker-disabled) .rs-picker-toggle:focus {
    border-color: ${({ theme }) => theme.forecast.activeText} !important;
  }
`

export const sharedTextFieldFocusStyles = css`
  --text-field-focus-color: ${({ theme }) => theme.forecast.border};

  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.forecast.border};
    outline-offset: -1px;
  }
`

export const sharedTextInputStyles = css<TextInputProps>`
  ${sharedTextFieldFocusStyles}

  & > input {
    padding: 0.25rem;
  }

  & > input::placeholder {
    color: ${({ $placeholderColor, theme }) =>
      $placeholderColor ? $placeholderColor : theme.forecast.secondaryText};
  }

  &::placeholder {
    color: ${({ $placeholderColor, theme }) =>
      $placeholderColor ? $placeholderColor : theme.forecast.secondaryText};
  }

  font-family: ${({ theme }) => theme.fontFamily.manrope};
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor ? $backgroundColor : theme.forecast.cardGroupBackground};
  border: ${(props) =>
    props.$borderColor
      ? `1px solid ${props.$borderColor}`
      : `1px solid ${props.theme.forecast.border2}`};
  border-radius: 0.25rem;
  font-weight: 600;
  box-shadow: none !important;
  opacity: 1;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    cursor: initial;
  }
`

export const TextInputStyled = styled(TextField.Root)<TextInputProps>`
  ${sharedTextInputStyles}
`

export const TextAreaStyled = styled(TextArea)<TextInputProps>`
  ${sharedTextInputStyles}
`

export const LinkStyled = styled(TextValue).attrs({ as: 'a' })`
  text-decoration: underline;
  color: ${({ theme }) => theme.forecast.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 20rem;
  @media (max-width: 40rem) {
    max-width: 5rem;
  }
`
